import { createSelector } from "reselect"
import { get, first, chain, merge, set, update } from "lodash"
import { getStartDate, getEndDate, getDriftDates } from "selectors/dates"
import { getIsActive } from "modules/portfolio-root/selectors/portfolios"
import { getBaseStartDate } from "modules/portfolio-root/selectors/base"

const EMPTY_ARRAY = []
const getBetaData = state => get(state, "global.data.beta.data.model.simulation.beta", EMPTY_ARRAY)
const getDynamicBetaData = state => get(state, "global.data.dynamicBeta.data.model.simulation.beta", EMPTY_ARRAY)
export const getBetaQueryDates = createSelector(
  [getIsActive, getDriftDates, getStartDate, getBaseStartDate],
  (isActive, driftDates, startDate, baseStartDate) => {
    let from = startDate < driftDates.startDate ? startDate : driftDates.startDate
    const to = driftDates.endDate

    if (isActive && from < baseStartDate) {
      from = baseStartDate
    }
    return { from, to }
  },
)

function getFactors(datum) {
  const { predicted, historical, custom } = datum
  const factors = {}

  if (predicted) {
    set(factors, "predicted", predicted)
  }
  if (historical) {
    set(factors, "historical", historical)
  }
  if (custom) {
    set(factors, "custom", custom)
  }

  return factors
}

// We need to combine the beta dates and dynamicBeta dates by date
// Because they are two independent queries, we can't guarantee that the array index/date will match
// So we first combine the two arrays and then reduce it using the date as the key
// combining the factors at the same time to obtain the following structure {'2018-01-01': { date: '2018-01-01', factors: { predicted: 1.2, historical: 1.1, custom: 1.3 } }, ...}
// Then we convert it to an array and sort it by date
export const getBeta = createSelector(
  [getBetaData, getDynamicBetaData],
  (betaData, dynamicBeta) => chain(betaData)
    .concat(dynamicBeta)
    .compact()
    .reduce((combined, datum) => update(combined, datum.date, (existing) => ({
      date: datum.date,
      factors: merge(get(existing, "factors", {}), getFactors(datum)),
    })), {})
    .map()
    .orderBy("date", "asc")
    .value(),
)

export const getFilteredBeta = createSelector(
  [getBeta, getStartDate, getEndDate],
  (beta, startDate, endDate) =>
    beta.filter((data) => {
      if (data && data.date >= startDate && data.date <= endDate) {
        return data
      } else {
        return null
      }
    }),
)

export const getFirst = createSelector(
  [getBeta],
  (beta) => first(beta),
)

export const getFirstBetaDate = createSelector(
  [getFilteredBeta],
  (beta) => get(first(beta), "date", null),
)
